import { If } from '@shared/ui/if.component';
import { useCwL10n } from '@shared/use-cw-L10n.hook';
import React, { useState } from 'react';
import Accordion from 'react-bootstrap/Accordion';
import Button from 'react-bootstrap/Button';
import Modal from 'react-bootstrap/Modal';
import { Urls } from '../../urls';
import css from './cookie-banner.component.less';

const layoutId = 'coreAppLoginPage';

export function CookieBanner(): React.ReactElement {

    const cwL10n = useCwL10n();
    const [displayBanner, setDisplayBanner] = useState(true);
    const [showCookieInfo, setShowCookieInfo] = useState(false);

    const cookiesUsed = [
        {
            name: 'CwAuthenticationTicket_Athena',
            description: cwL10n.get('This cookie is used for authentication in our website.', 'text_label_cookieAuthDesc', layoutId),
            status: cwL10n.get('Necessary', 'text_label_cookieStatusNecessary', layoutId),
        },
        {
            name: 'cw_login',
            description: cwL10n.get('This cookie is a part of authentication in our website.', 'text_label_cookieOAuthDesc', layoutId),
            status: cwL10n.get('Necessary', 'text_label_cookieStatusNecessary', layoutId),
        },
        {
            name: 'cw-reqtkn',
            description: cwL10n.get('This cookie is a part of security protection in our website.', 'text_label_cookieCSRFDesc', layoutId),
            status: cwL10n.get('Necessary', 'text_label_cookieStatusNecessary', layoutId),
        },
        {
            name: 'cw_mfa_remember',
            description: cwL10n.get('This cookie is used for multi-factor authentication in our website.', 'text_label_cookieMFADesc', layoutId),
            status: cwL10n.get('Necessary', 'text_label_cookieStatusNecessary', layoutId),
        },
        {
            name: '*_CLIENT_SESSION_ID',
            description: cwL10n.get('These cookies are set by each of our applications to help cache application data for faster performance.', 'text_label_cookieDataSessionDesc', layoutId),
            status: cwL10n.get('Necessary', 'text_label_cookieStatusNecessary', layoutId),
        },
        {
            name: 'AGS_ROLES',
            description: cwL10n.get('This is a Third Party cookie used for authentication for the interactive maps in the website.', 'text_label_cookieEsriDesc', layoutId),
            status: cwL10n.get('Necessary', 'text_label_cookieStatusNecessary', layoutId),
        },
        {
            name: 'JSESSIONID',
            description: cwL10n.get('This is a Third Party cookie used for monitoring website performance and errors.', 'text_label_cookieNewRelicDesc', layoutId),
            status: cwL10n.get('Necessary', 'text_label_cookieStatusNecessary', layoutId),
        }
    ];

    function acceptCookieConsentSetting(): void {
        setShowCookieInfo(false);
        setDisplayBanner(false);
    }

    const cookieDetailItems = cookiesUsed.map((cookie, i) => (
        <Accordion.Item eventKey={cookie.name} key={cookie.name}>
            <Accordion.Header>
                <strong>{cookie.name}</strong>
            </Accordion.Header>
            <Accordion.Body className={css.bodyText}>
                <dl>
                    <dt>{cwL10n.get('Category', 'text_label_cookieCategory', layoutId)}</dt>
                    <dd>{cookie.status}</dd>
                    <dt>{cwL10n.get('Description', 'text_label_cookieDescription', layoutId)}</dt>
                    <dd>{cookie.description}</dd>
                </dl>
            </Accordion.Body>
        </Accordion.Item>
    ));

    return (
        <>
            <If
                if={displayBanner}
                then={() =>
                    <div
                        role="dialog"
                        tabIndex={0}
                        aria-modal="true"
                        aria-label={cwL10n.get('Cookie Banner', 'text_label_cookieBanner', layoutId)}
                        className={css.cookieBanner}>
                        <div className={css.cookieText}>
                            {cwL10n.get('We use cookies to deliver and optimize the functionality on our websites. We only use cookies that are needed in order to give you the best possible experience. We respect your preferences regarding cookies on our websites.', 'text_label_cookieConsent', layoutId)}
                        </div>
                        <div className={css.cookieButtons}>
                            <Button
                                variant='secondary'
                                data-cw-id='btnShowCookieSettings'
                                onClick={() => setShowCookieInfo(true)}>
                                {cwL10n.get('Cookie Info', 'ctl_btn_btnShowCookieSettings_buttonText', layoutId)}
                            </Button>
                            <Button
                                variant='primary'
                                data-cw-id='btnAcceptCookies'
                                onClick={() => { acceptCookieConsentSetting(); }}>
                                {cwL10n.get('I Accept', 'ctl_btn_btnAcceptCookies_buttonText', layoutId)}
                            </Button>
                        </div>
                    </div>
                }
            />
            <Modal
                show={showCookieInfo}
                onHide={() => setShowCookieInfo(false)}>
                <Modal.Header closeButton><img className={css.modalTitle} src='assets/logos/logo-blue.png' alt='' /></Modal.Header>
                <Modal.Body>
                    <>
                        <div className={css.titleText}>{ cwL10n.get('Do Not Track My Personal Data', 'text_header_cookieConsent', layoutId)}</div>
                        <div className={css.bodyText}>
                            { cwL10n.get('When you visit our website, we store cookies on your browser to collect information. The information collected might relate to you, your preferences, or your device, and is only used to make the site work as you expect it to and to provide a more personalized web experience. For more information about the First and Third Party Cookies used please follow this link.', 'text_body_cookieConsent', layoutId) }
                        </div>
                        <div>
                            <a
                                className={css.privacyNotice}
                                href={Urls.privacyNotice}
                                target='_blank'
                                rel='noopener noreferrer'>
                                { cwL10n.get('Privacy Notice', 'ctl_btn_btnShowPrivacyNotice_buttonText', layoutId) }
                            </a>
                        </div>
                        <br/>
                        <div>{ cwL10n.get('Cookie Details', 'ctl_modal_cookieDetails_header', layoutId) }</div>
                        <Accordion alwaysOpen>
                            {cookieDetailItems}
                        </Accordion>
                    </>
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant='primary'
                        data-cw-id='btnConfirmCookieSettings'
                        onClick={() => acceptCookieConsentSetting()}>
                        {cwL10n.get('I Accept', 'ctl_btn_btnAcceptCookies_buttonText', layoutId)}
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
}
